import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { ObjectData } from './ParserTypes';
import extractErrorMsg from '../helpers/extractErrorMsg';
import {
  selectTransientObjects,
  setTransientObjects,
} from '../../reducers/documentation/documentationReducer';
import {
  addParsedDataToDocumentation,
  selectEnvironments,
  selectParserInfo,
  selectSaltoObjectTypes,
} from '../../reducers/global/globalReducer';
import { calcParsedFields, calcPills } from './parserUtils';
import { GlobalDto } from '../../reducers/global/globalReducerTypes';
import { useFetchGlobal } from '../../hooks/global-reducer/useFetchGlobal';
import { telemetry } from '../../telemetry';
import { useDocumentationApiFacade } from '../../apis/facades/useDocumentationApiFacade';

export enum GetParsedObjectStatus {
  addToConfigurationCanvas = 'addToConfigurationCanvas',
  fetchData = 'fetchData',
}

interface UseParserProps {
  parseObject: (objectName: string) => Promise<ObjectData[] | undefined>;
  parseObjectOnDemand: (objectType: string) => void;
  objectsBeingParsed: { [objectName: string]: boolean };
  toggleObjectVisibility: (objectType: string, visible: boolean, _fetchGlobal?: boolean) => void;
  parseObjectIfNotParsed: (objectName: string) => Promise<void>;
}

const useParser = ({ crmOrgId }: { crmOrgId: string }): UseParserProps => {
  const { fetchGlobal } = useFetchGlobal();

  const dispatch = useDispatch();
  const { post_createObject, patch_toggleObjectVisibility } = useDocumentationApiFacade();

  const globalEnvironments = useSelector(selectEnvironments);
  const parserInfo = useSelector(selectParserInfo(crmOrgId));

  const parsedObjectNames = useMemo(
    () => parserInfo?.parsedObjectNames ?? [],
    [parserInfo?.parsedObjectNames],
  );

  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const objectsBeingParsed = useSelector(selectTransientObjects(crmOrgId));

  const toggleObjectVisibility = useCallback(
    async (objectName: string, visible: boolean, _fetchGlobal?: boolean) => {
      try {
        await patch_toggleObjectVisibility(objectName, visible);

        if (_fetchGlobal) {
          await fetchGlobal({ crmOrgId, skipLoadingObjectTypes: true });
        }
      } catch (error) {
        telemetry.captureError(error, { message: extractErrorMsg(error) });
      }
    },
    [crmOrgId, patch_toggleObjectVisibility, fetchGlobal],
  );

  const toggleIsObjectBeingParsed = useCallback(
    ({ objectName, isBeingParsed }: { objectName: string; isBeingParsed: boolean }) => {
      dispatch(
        setTransientObjects({
          objectName: objectName,
          isObjectBeingParsed: isBeingParsed,
          crmOrgId,
        }),
      );
    },
    [crmOrgId, dispatch],
  );

  //this call saves object info in parser table
  const parseObject = useCallback(
    async (objectName: string) => {
      const newObject = saltoObjectTypes?.find((object) => object.objectType === objectName);
      const isObjectAlreadyParsed = parsedObjectNames.includes(objectName);

      if (newObject && crmOrgId) {
        const objectTypeName = newObject.objectType;

        toggleIsObjectBeingParsed({ objectName, isBeingParsed: true });

        try {
          let data = {} as ObjectData;

          if (isObjectAlreadyParsed) {
            await toggleObjectVisibility(objectName, true);
          } else {
            data = await post_createObject(objectTypeName, { payload: { visible: true } });
          }
          await fetchGlobal({ crmOrgId, skipLoadingObjectTypes: true });
          toggleIsObjectBeingParsed({ objectName, isBeingParsed: false });

          return data ? [data] : [];
        } catch (e) {
          toggleIsObjectBeingParsed({ objectName, isBeingParsed: false });
          telemetry.captureError(e, { message: extractErrorMsg(e) });
        }
      }
    },
    [
      crmOrgId,
      post_createObject,
      saltoObjectTypes,
      toggleObjectVisibility,
      parsedObjectNames,
      fetchGlobal,
      toggleIsObjectBeingParsed,
    ],
  );

  //this call doesn't save object info in parser table
  const parseObjectOnDemand = useCallback(
    async (objectApiName: string) => {
      const rollups = globalEnvironments?.[crmOrgId]?.data?.rollups;

      if (!parsedObjectNames.includes(objectApiName)) {
        toggleIsObjectBeingParsed({ objectName: objectApiName, isBeingParsed: true });

        try {
          const response = await post_createObject(objectApiName, {
            payload: { visible: false },
          });
          const objectsData = response ? [response] : [];
          const fieldsParsedOnDemand = calcParsedFields(objectsData, rollups ?? []);

          //update store with new info
          const pills = calcPills({
            documentation: { parsedObjects: objectsData },
          } as GlobalDto);

          dispatch(
            addParsedDataToDocumentation({
              crmOrgId,
              objectName: objectApiName,
              response: objectsData,
              fieldsParsedOnDemand,
              pills,
            }),
          );
          toggleIsObjectBeingParsed({ objectName: objectApiName, isBeingParsed: false });
          return objectsData;
        } catch (error) {
          toggleIsObjectBeingParsed({ objectName: objectApiName, isBeingParsed: false });
          telemetry.captureError(error, { message: extractErrorMsg(error) });
        }
      }
    },
    [
      dispatch,
      post_createObject,
      toggleIsObjectBeingParsed,
      crmOrgId,
      globalEnvironments,
      parsedObjectNames,
    ],
  );

  const parseObjectIfNotParsed = useCallback(
    async (objectName: string) => {
      if (!parsedObjectNames.includes(objectName)) {
        await parseObjectOnDemand(objectName);
      }
    },
    [parseObjectOnDemand, parsedObjectNames],
  );

  return {
    objectsBeingParsed,
    parseObject,
    parseObjectOnDemand,
    toggleObjectVisibility,
    parseObjectIfNotParsed,
  };
};

export default useParser;
